import { Box, Stack, SxProps, Theme } from "@mui/material";
import { ResponsiveStyleValue } from "@mui/system";
import moment, { Moment } from "moment";
import { useMemo } from "react";

import { DATETIME_FORMATS } from "@/common/constants/common";
import { DatetimeHelper } from "@/common/helpers/datetime";

import AppTypography from "../Text/AppTypography";

const defaultFormat = DATETIME_FORMATS.DISPLAY_DATETIME;

interface DatetimeProps {
  datetime?: string | Date | Moment | null;
  /** Moment format syntax */
  format?: string | null;
  direction?: ResponsiveStyleValue<"column" | "row" | "row-reverse" | "column-reverse">;
  withFallbackToNow?: boolean;
  withDurationFromNow?: boolean;
  sx?: SxProps<Theme>;
}

/** Displays datetime */
export default function Datetime({
  datetime,
  format,
  direction = "row",
  withFallbackToNow = false,
  withDurationFromNow = false,
  sx,
}: DatetimeProps) {
  const datetimeM = useMemo(
    () => (datetime && moment(datetime)) || (withFallbackToNow ? moment() : null),
    [datetime, withFallbackToNow],
  );
  const datetimeFormatted = useMemo(
    () => (datetimeM ? datetimeM.format(format || defaultFormat) : null),
    [datetimeM, format],
  );

  if (!datetime || !datetimeFormatted) {
    return null;
  }

  return (
    <Stack
      component='span'
      direction={direction}
      gap={direction === "row" ? 0.5 : 0}
      sx={{
        display: "inline-flex",
        ...((direction === "row" && { alignItems: "center", justifyContent: "start" }) || {}),
        ...((direction === "column" && { alignItems: "start", justifyContent: "start" }) || {}),
        ...sx,
      }}
    >
      <Box component='span'>{datetimeFormatted}</Box>{" "}
      {withDurationFromNow && (
        <AppTypography
          ellipsing={{ enabled: true }}
          component='span'
          color='text.secondary'
          isInheritFontStyle
        >
          ({DatetimeHelper.humanizeDateRangeDurationFromNow(datetimeM, { isSuffix: true })})
        </AppTypography>
      )}
    </Stack>
  );
}
